// ════════════════════════════════════════════════════════════════
// FloorCost — Stale-chunk recovery
// ────────────────────────────────────────────────────────────────
// Every feature past the initial bundle is a lazy dynamic import —
// Tool Kit, Catalog, Measure, Insights, Monthly, print, uPlot, and
// even app.js itself (loaded by boot.js). index.html is served
// network-first but the hashed JS/CSS chunks are cache-first
// (public/sw.js). So a fresh Netlify deploy leaves a still-open
// session holding the OLD chunk URLs: opening a not-yet-loaded tab
// requests e.g. `toolkit-<oldhash>.js`, which the deploy purged →
// 404 → the dynamic import rejects → the pane renders blank until the
// operator manually reloads (a reload network-fetches the new
// index.html, which references the new hashes).
//
// The fix: on any chunk-load failure, reload ONCE to pull the fresh
// shell. Vite dispatches `vite:preloadError` on the window for exactly
// this, so a single global listener heals every lazy import; the
// per-feature catch blocks also call reloadForStaleChunk() as
// belt-and-braces. A short reload-suppression window stops a
// genuinely-broken chunk (or an offline device with nothing cached)
// from looping — after one attempt it degrades to the feature's own
// error path.
//
// The real precache (vite-plugin-pwa / InjectManifest, SOTA_2026 T4-1)
// would make the old chunks available offline and remove most of this
// exposure; until then this is the recovery.
// ════════════════════════════════════════════════════════════════

const RELOAD_KEY = 'fc:chunk-reload-at';
const RELOAD_SUPPRESS_MS = 20_000;

/**
 * True when `err` looks like a failed dynamic import / chunk fetch — the
 * cross-engine message set browsers use for a module that 404'd or otherwise
 * failed to load.
 * @param {unknown} err
 * @returns {boolean}
 */
export function isChunkLoadError(err) {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module/i.test(
    msg,
  );
}

/**
 * Reload the page to recover from a stale chunk — at most once per
 * suppression window, so a persistently-broken chunk degrades to the
 * feature's own error path instead of a reload loop.
 * @returns {boolean} true if a reload was triggered
 */
export function reloadForStaleChunk() {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  } catch {
    // sessionStorage blocked (private mode / disabled) → we have no reliable
    // cross-reload guard, so DON'T auto-reload: a blank pane the operator can
    // reload by hand is far better than risking a reload loop. This is the
    // pre-fix behaviour, scoped to the rare no-storage case.
    return false;
  }
  if (Number.isFinite(last) && Date.now() - last < RELOAD_SUPPRESS_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Register the global `vite:preloadError` listener. Call once, as early as
 * possible in boot — before the first dynamic import (even app.js) — so a
 * stale app-shell chunk self-heals too. Deliberately does NOT
 * `preventDefault()`: letting Vite reject the import keeps each feature's own
 * catch working; we are navigating away regardless.
 */
export function installChunkReloadGuard() {
  window.addEventListener('vite:preloadError', () => {
    reloadForStaleChunk();
  });
}
